import { ReportSection } from '../hooks/usePdfExportState';

// Tailwind colors used for report sub-block accents.
const COLORS = 'blue|green|red|indigo|amber|emerald|purple|orange|yellow|teal|cyan|rose|pink|sky|violet|lime';

// A colored block is signalled by a tinted background (bg-{color}-50/100),
// a colored left accent bar (border-l-4 + border-{color}-{300..600}),
// or a colored gradient. Neutral stat cards (bg-white / bg-gray-*) carry none
// of these signals, so they are naturally excluded.
const COLORED_BG = new RegExp(`(?:^|\\s)bg-(?:${COLORS})-(?:50|100)(?:\\s|$)`);
const COLORED_LEFT_BAR = new RegExp(`border-l-4`);
const COLORED_BORDER = new RegExp(`border-(?:${COLORS})-(?:300|400|500|600)`);
const COLORED_GRADIENT = new RegExp(`bg-gradient-to-\\S+`);
const GRADIENT_FROM = new RegExp(`from-(?:${COLORS})-`);

function isColoredBlock(el: Element): boolean {
  const cls = el.className;
  if (typeof cls !== 'string') return false;
  if (COLORED_BG.test(cls)) return true;
  if (COLORED_LEFT_BAR.test(cls) && COLORED_BORDER.test(cls)) return true;
  if (COLORED_GRADIENT.test(cls) && GRADIENT_FROM.test(cls)) return true;
  return false;
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// Derive a human label: prefer the block's first heading, else its first words.
function deriveLabel(el: Element): string {
  const heading = el.querySelector('h3, h4, h2');
  const fromHeading = heading?.textContent?.trim();
  if (fromHeading) return fromHeading;
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ');
  if (!text) return 'Block';
  return text.split(' ').slice(0, 6).join(' ');
}

/**
 * Walk the rendered report DOM and discover removable colored sub-blocks.
 *
 * For each top-level section ([data-section-id]) we claim the OUTERMOST colored
 * blocks (a colored block nested inside another claimed block is skipped, so a
 * section-level colored container wins over its colored sub-cards). Each claimed
 * block is tagged with `data-block-id` so the removal overlay can target it, and
 * returned as a `kind:'block'` child of its parent section.
 *
 * This mutates the DOM (sets data-block-id) and must be re-run whenever the
 * report tree re-renders (which recreates the nodes and drops the attribute).
 */
export function discoverColoredBlocks(root: HTMLElement | null): ReportSection[] {
  if (!root) return [];

  const blocks: ReportSection[] = [];
  const usedIds = new Set<string>();
  const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-section-id]'));

  for (const section of sections) {
    const sectionId = section.getAttribute('data-section-id');
    if (!sectionId) continue;

    // Candidate divs in document order, outermost claimed first.
    const candidates = Array.from(section.querySelectorAll<HTMLElement>('div'));
    const claimed: HTMLElement[] = [];

    for (const el of candidates) {
      if (!isColoredBlock(el)) continue;
      // Skip if a colored ancestor (within this section) was already claimed,
      // or if it sits inside a nested section.
      if (claimed.some(c => c.contains(el))) continue;
      const nearestSection = el.closest('[data-section-id]');
      if (nearestSection !== section) continue;

      claimed.push(el);

      const label = deriveLabel(el);
      let id = `${sectionId}::${slug(label)}`;
      let n = 2;
      while (usedIds.has(id)) {
        id = `${sectionId}::${slug(label)}-${n++}`;
      }
      usedIds.add(id);

      el.dataset.blockId = id;
      blocks.push({
        id,
        title: label,
        parentId: sectionId,
        isChild: true,
        kind: 'block'
      });
    }
  }

  return blocks;
}
